import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { getRequest, postRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import Pill from "../../Component/Pill";
import AffiliateLayout, { SETTLE_STATUS, inr } from "./components/AffiliateLayout";

interface Totals {
    clients: number;
    billed: number;
    metaCost: number;
    profit: number;
    dueCount: number;
    dueAmount: number;
    approvedCount: number;
    approvedAmount: number;
}

interface Period {
    period: string;
    label: string;
    ready: boolean;
    readyAt: string;
}

const EMPTY_TOTALS: Totals = { clients: 0, billed: 0, metaCost: 0, profit: 0, dueCount: 0, dueAmount: 0, approvedCount: 0, approvedAmount: 0 };

// Monthly messaging commission for credit-line clients:
// our messaging profit (billed − Meta cost) × the affiliate tier's messaging %. Admin approves per month.
const MessagingCommission = () => {
    // Default to last month — the current month is never ready
    const [month, setMonth] = useState(moment().subtract(1, "month").format("YYYY-MM"));
    const [rows, setRows] = useState<any[]>([]);
    const [totals, setTotals] = useState<Totals>(EMPTY_TOTALS);
    const [period, setPeriod] = useState<Period | null>(null);
    const [isLoader, setIsLoader] = useState<boolean>(true);
    const [approving, setApproving] = useState<string | null>(null); // user id, or "all"

    const getReport = useCallback(async () => {
        if (!month) return;
        const [y, m] = month.split("-");
        try {
            setIsLoader(true);
            const response = await getRequest(`/affiliate/messaging-commission?year=${y}&month=${Number(m)}`);

            if (response.status === "success") {
                setRows(response.data.rows || []);
                setTotals(response.data.totals || EMPTY_TOTALS);
                setPeriod(response.data.period || null);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading messaging commission!");
        } finally {
            setIsLoader(false);
        }
    }, [month]);

    useEffect(() => {
        getReport();
    }, [getReport]);

    const approve = async (userIds: string[], key: string) => {
        const [y, m] = month.split("-");
        try {
            setApproving(key);
            const response = await postRequest("/affiliate/messaging-commission/approve", { year: y, month: Number(m), userIds });

            if (response.status === "success") {
                toast.success(response.message);
            } else if (response.status === "session_expired") {
                logout();
                return;
            } else {
                toast.error(response.message);
            }
            (response.data?.failed || []).slice(0, 3).forEach((f: any) => {
                const row = rows.find(r => r.user._id === f.userId);
                toast.warning(`${row ? clientLabel(row.user) : "Client"}: ${f.reason}`);
            });
            getReport();
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        } finally {
            setApproving(null);
        }
    };

    const readyRows = rows.filter(r => r.eligible);

    const approveAll = () => {
        if (!readyRows.length) return;
        if (!window.confirm(`Approve messaging commission for ${readyRows.length} client${readyRows.length > 1 ? "s" : ""} (${inr(totals.dueAmount)}) for ${period?.label}? Each amount is added to the affiliate's balance.`)) return;
        approve(readyRows.map(r => r.user._id), "all");
    };

    const ready = !!period?.ready;
    const readyNote = period && !ready ? `${period.label} data is final from ${moment(period.readyAt).format("D MMM YYYY")}` : "";

    return (
        <AffiliateLayout
            loading={isLoader}
            actions={<>
                <input
                    className="cx-input mono"
                    type="month"
                    value={month}
                    max={moment().format("YYYY-MM")}
                    onChange={(e) => setMonth(e.target.value)}
                />
                <button
                    type="button"
                    className="cx-btn cx-btn-primary"
                    onClick={approveAll}
                    disabled={!ready || !readyRows.length || !!approving}
                    title={readyNote || undefined}
                >
                    <i className="ri-check-double-line"></i>{approving === "all" ? "Approving…" : `Approve all ready (${readyRows.length})`}
                </button>
            </>}
            stats={[
                { label: "Messaging billed", value: inr(totals.billed, 0), icon: "ri-message-3-line", delta: `${totals.clients} clients` },
                { label: "Meta cost", value: inr(totals.metaCost, 0), icon: "ri-bank-card-line", delta: period?.label || "" },
                { label: "Our profit", value: inr(totals.profit, 0), icon: "ri-funds-line", delta: "Billed − Meta cost" },
                { label: "Commission due", value: inr(totals.dueAmount), icon: "ri-time-line", delta: `${totals.dueCount} ready to approve` },
                { label: "Approved", value: inr(totals.approvedAmount), icon: "ri-checkbox-circle-line", delta: `${totals.approvedCount} clients` },
            ]}
        >
            <div className="cx-card">
                {readyNote && (
                    <div className="cx-toolbar">
                        <span className="fs12 muted"><i className="ri-information-line"></i> {readyNote} — Meta's usage data can lag up to 2 days, so approval opens then.</span>
                    </div>
                )}

                <div className="cx-tbl-wrap">
                    <table className="cx-tbl">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Affiliate</th>
                                <th>Tier</th>
                                <th className="r">Messages</th>
                                <th className="r">Billed</th>
                                <th className="r">Meta cost</th>
                                <th className="r">Profit</th>
                                <th className="r">Commission</th>
                                <th>Status</th>
                                <th className="r">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? (
                                rows.map((item) => (
                                    <tr key={item.user._id}>
                                        <td>
                                            <b style={{ fontWeight: 600 }}>{clientLabel(item.user)}</b>
                                            <div className="fs12 muted">{item.user.email}</div>
                                        </td>
                                        <td>
                                            {item.affiliate ? <>
                                                {item.affiliate.name || "—"}
                                                {item.affiliate.company && <div className="fs12 muted">{item.affiliate.company}</div>}
                                            </> : <span className="muted">—</span>}
                                        </td>
                                        <td>
                                            {item.tier?.title || <span className="muted">—</span>}
                                            {item.tier && <div className="fs12 muted mono">{item.rate}% of profit</div>}
                                        </td>
                                        <td className="r mono">{Number(item.volume || 0).toLocaleString("en-IN")}</td>
                                        <td className="r mono">{inr(item.billed)}</td>
                                        <td className="r mono muted">{inr(item.metaCost)}</td>
                                        <td className="r mono">{inr(item.profit)}</td>
                                        <td className="r mono" style={{ fontWeight: 600 }}>{item.approved || item.eligible ? inr(item.commissionAmount) : <span className="muted">—</span>}</td>
                                        <td>
                                            <StatusCell item={item} ready={ready} />
                                        </td>
                                        <td>
                                            <div className="cx-ra">
                                                {item.eligible && (
                                                    <button
                                                        type="button"
                                                        title={ready ? "Approve commission" : readyNote}
                                                        disabled={!ready || !!approving}
                                                        style={!ready ? { opacity: .4, cursor: "default" } : undefined}
                                                        onClick={() => approve([item.user._id], item.user._id)}
                                                    >
                                                        <i className={approving === item.user._id ? "ri-loader-4-line" : "ri-check-line"}></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={10}>
                                        <div className="cx-empty">
                                            <i className="ri-message-3-line"></i>
                                            {isLoader ? "Loading usage from Meta…" : "No credit-line clients with an affiliate."}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="cx-toolbar">
                    <span className="fs12 muted">
                        Credit-line clients with an affiliate. Commission = our messaging profit (what we billed − Meta's cost, from Meta's usage data) × the tier's messaging %.
                        Approved commissions go to the affiliate's balance and are paid out with their next payout request.
                    </span>
                </div>
            </div>
        </AffiliateLayout>
    );
};

const clientLabel = (u: any) => u?.company || u?.name || u?.email || "—";

const StatusCell = ({ item, ready }: { item: any; ready: boolean }) => {
    if (item.approved) {
        const [, label] = SETTLE_STATUS[item.commission?.ac_status] || ["none", ""];
        return <>
            <Pill tone="ok">Approved</Pill>
            <div className="fs12 muted">
                <span className="mono">#{item.commission?.ac_unique_id || ""}</span>
                {label && ` · ${label}`}{item.commission?.ac_status === 0 && item.commission?.ac_payout_id ? " · in a payout" : ""}
            </div>
        </>;
    }
    if (item.eligible) return <Pill tone={ready ? "blue" : "warn"}>{ready ? "Ready" : "Waiting"}</Pill>;
    return <>
        <Pill tone="none">Not eligible</Pill>
        <div className="fs12 muted" style={{ maxWidth: 220 }}>{item.reason}</div>
    </>;
};

export default MessagingCommission;
